/**
 * round7/signup-ui (P4.5-B) — the ONE config module SignupPage.tsx sources
 * its consent checkbox from, per the task brief: "a future document update
 * is a config change, not a code change." Real, already-legally-confirmed
 * documents (Termly, confirmed sufficient for launch by the product owner —
 * NOT placeholders — see docs/planning/DockPro-Phase4.5-Trial-SelfReg-
 * Prompts.md's H-3 resolution, 2026-09-02, in the sibling DockPro repo).
 *
 * `policyVersion` is the literal value H-3's resolution locked in: a single
 * combined tag covering all three documents as one consent bundle (the
 * signup checkbox reads "I agree to the Terms, Privacy Policy, and Cookie
 * Policy" as ONE acceptance, so one version string for the whole bundle is
 * simpler to reason about than three independently-versioned tags that could
 * drift out of sync). Bump this to a new value whenever any of the three
 * documents changes in Termly in a way that matters legally — that plan doc
 * is also where the bump trigger (Termly's own dashboard last-updated date)
 * is recorded.
 *
 * `CONSENT_TEXT_SHOWN` is built FROM `CONSENT_DOCUMENTS`' own labels rather
 * than a hand-typed duplicate sentence, so a future document rename can't
 * silently diverge the two — this is what registerHandler.ts's
 * `consent.textShown` field actually records (a plain-text audit string,
 * not markup); SignupPage.tsx's on-page checkbox label renders the same
 * three names as real links to the same URLs, built from this same object.
 */

export const CONSENT_POLICY_VERSION = "2026-09-v1";

export interface ConsentDocument {
  label: string;
  url: string;
}

export const CONSENT_DOCUMENTS: {
  terms: ConsentDocument;
  privacy: ConsentDocument;
  cookie: ConsentDocument;
} = {
  terms: {
    label: "Terms and Conditions",
    url: "https://app.termly.io/policy-viewer/policy.html?policyUUID=7a1586ed-bf95-4413-b291-6b248459c81b",
  },
  privacy: {
    label: "Privacy Policy",
    url: "https://app.termly.io/policy-viewer/policy.html?policyUUID=fb9f2c23-24a5-4b22-94c7-1c718d4e2747",
  },
  cookie: {
    label: "Cookie Policy",
    url: "https://app.termly.io/policy-viewer/policy.html?policyUUID=d0cd950f-b759-4ce4-9736-e1446f4531b4",
  },
};

// The exact plain-text sentence recorded as `consent.textShown` on submit
// (registerHandler.ts's RegistrationConsent contract: {policyVersion,
// textShown} — no timestamp; the server stamps that at write time). Kept as
// a single derived constant so SignupPage.tsx never hand-composes this
// sentence itself.
export const CONSENT_TEXT_SHOWN =
  `I agree to the ${CONSENT_DOCUMENTS.terms.label}, ${CONSENT_DOCUMENTS.privacy.label}, ` +
  `and ${CONSENT_DOCUMENTS.cookie.label}.`;
